// eslint-disable-next-line import/no-restricted-paths
import isEqual from "lodash.isequal";
import { useCallback, useEffect, useMemo, useState } from "react";

import { getStringHTML } from "@/lib/aws-amplify/storage";

import { useLoadingScreen } from "@/components/features/side-effect/show-loading-screen-to-user/_hooks/use-loading-screen";

import type * as Type from "../../type";
// eslint-disable-next-line import/no-restricted-paths
import type { FetchResult } from "@apollo/client";

type Props = {
	article:
		| {
				__typename?: "S3Object" | undefined;
				bucket: string;
				key: string;
				region: string;
				mimeType?: string | null | undefined;
				fileName?: string | null | undefined;
		  }
		| null
		| undefined;
	tagList: Array<string>;
	regionTag?: string;
	prefectureTag?: string;
	cityTag?: string;
	onUpdate: ({
		articleHTML,
		tags,
		tagsLocation,
	}: {
		articleHTML: string;
		tags: Array<string>;
		tagsLocation: Array<string>;
	}) => Promise<FetchResult<unknown>>;
	onUpdateSuccessCallback: () => void;
};

/**
 * lang = default
 */
export const useCreateMagazineEditorForDefaultModel = ({
	article,
	tagList,
	regionTag,
	prefectureTag,
	cityTag,
	onUpdate: _onUpdate,
	onUpdateSuccessCallback,
}: Props) => {
	const [state, setState] = useState<Type.ViewModel.ForDefaultViewModel>({
		articleHTMLObject: {
			initialArticleHTML: "",
			articleHTML: "",
		},
		tag: {
			initialTagList: tagList,
			tagList,
			initialRegionTag: regionTag,
			regionTag,
			initialPrefectureTag: prefectureTag,
			prefectureTag,
			initialCityTag: cityTag,
			cityTag,
		},
	});

	const isDirty = useMemo(
		() =>
			!isEqual(state.articleHTMLObject.initialArticleHTML, state.articleHTMLObject.articleHTML) ||
			!isEqual(state.tag.initialTagList, state.tag.tagList) ||
			!isEqual(state.tag.initialRegionTag, state.tag.regionTag) ||
			!isEqual(state.tag.initialPrefectureTag, state.tag.prefectureTag) ||
			!isEqual(state.tag.initialCityTag, state.tag.cityTag),
		[
			state.articleHTMLObject.articleHTML,
			state.articleHTMLObject.initialArticleHTML,
			state.tag.cityTag,
			state.tag.initialCityTag,
			state.tag.initialPrefectureTag,
			state.tag.initialRegionTag,
			state.tag.initialTagList,
			state.tag.prefectureTag,
			state.tag.regionTag,
			state.tag.tagList,
		],
	);
	const isValid = isDirty;

	useEffect(() => {
		const fetchPlanArticle = async () => {
			const res = await getStringHTML(article);

			setState((prev) => ({ ...prev, articleHTMLObject: { initialArticleHTML: res, articleHTML: res } }));
		};

		fetchPlanArticle();
	}, [article]);

	const onSetArticleHTML = useCallback((html: string) => {
		setState((prev) => ({ ...prev, articleHTMLObject: { ...prev.articleHTMLObject, articleHTML: html } }));
	}, []);

	const resetArticleHTML = useCallback((articleHTML: string, tag: Type.ViewModel.ForDefaultViewModel["tag"]) => {
		setState((prev) => ({
			...prev,
			articleHTMLObject: { initialArticleHTML: articleHTML, articleHTML },
			tag,
		}));
	}, []);

	const onSetTag = useCallback(
		({
			tagList,
			regionTag,
			prefectureTag,
			cityTag,
		}: {
			tagList: Array<string>;
			regionTag?: string;
			prefectureTag?: string;
			cityTag?: string;
		}) => {
			setState((prev) => ({
				...prev,
				tag: {
					...prev.tag,
					tagList,
					regionTag,
					prefectureTag,
					cityTag,
				},
			}));
		},
		[],
	);

	const { showLoadingScreenCallback, hideLoadingScreenCallback } = useLoadingScreen();
	const onUpdate = useCallback(async () => {
		showLoadingScreenCallback();
		const tagsLocation = [];
		if (state.tag.regionTag) tagsLocation.push(state.tag.regionTag);
		if (state.tag.prefectureTag) tagsLocation.push(state.tag.prefectureTag);
		if (state.tag.cityTag) tagsLocation.push(state.tag.cityTag);

		const { data, errors } = await _onUpdate({
			articleHTML: state.articleHTMLObject.articleHTML,
			tags: state.tag.tagList,
			tagsLocation,
		}).finally(hideLoadingScreenCallback);

		if (data && !errors) {
			resetArticleHTML(state.articleHTMLObject.articleHTML, state.tag);
			onUpdateSuccessCallback();
		}
	}, [
		_onUpdate,
		hideLoadingScreenCallback,
		onUpdateSuccessCallback,
		resetArticleHTML,
		showLoadingScreenCallback,
		state.articleHTMLObject.articleHTML,
		state.tag,
	]);

	return {
		articleHTML: state.articleHTMLObject.articleHTML,
		onSetArticleHTML,
		tag: {
			tagList: state.tag.tagList,
			regionTag: state.tag.regionTag,
			prefectureTag: state.tag.prefectureTag,
			cityTag: state.tag.cityTag,
		},
		onSetTag,
		isDirty,
		isValid,
		onUpdate,
	};
};
